import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2, Plus } from "lucide-react";
import {
  AdvisoryDraftStatus,
  AdvisoryTljDraft,
  useCosmosClient
} from "@/lib/cosmos-api";
import type { PromptAttachment } from "@/lib/prompt-attachments";
import {
  Button,
  Card,
  EmptyState,
  ErrorBlock,
  PageHeader
} from "@/components/ui-ext/primitives";
import { AdvisoryBriefComposer } from "@/components/advisory/AdvisoryBriefComposer";
import { AdvisoryDraftsList } from "@/components/advisory/AdvisoryDraftsList";
import { TljDraftView } from "@/components/advisory/TljDraftView";

type GenerationState =
  | { phase: "idle" }
  | { phase: "streaming"; steps: string[]; deltaChars: number }
  | { phase: "error"; message: string };

export function AdvisoryPage() {
  const cosmos = useCosmosClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { draft?: string };

  const [drafts, setDrafts] = useState<AdvisoryTljDraft[]>([]);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdvisoryTljDraft | null>(null);
  const [composing, setComposing] = useState(true);
  const [generation, setGeneration] = useState<GenerationState>({ phase: "idle" });
  const abortRef = useRef<AbortController | null>(null);

  const refreshDrafts = useCallback(async (): Promise<AdvisoryTljDraft[]> => {
    try {
      const response = await cosmos.listAdvisoryDrafts({ limit: 50 });
      setDrafts(response.drafts);
      setDraftsError(null);
      return response.drafts;
    } catch (error) {
      setDraftsError(
        error instanceof Error ? error.message : "Could not load drafts."
      );
      return [];
    }
  }, [cosmos]);

  useEffect(() => {
    void (async () => {
      const loaded = await refreshDrafts();
      const requested = search.draft
        ? loaded.find((entry) => entry.id === search.draft)
        : undefined;
      if (requested) {
        setSelected(requested);
        setComposing(false);
      }
    })();
    return () => abortRef.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectDraft = (draft: AdvisoryTljDraft | null) => {
    setSelected(draft);
    setComposing(draft === null);
    void navigate({
      to: "/advisory",
      search: draft ? { draft: draft.id } : {},
      replace: true
    });
  };

  const generate = async (input: {
    prompt: string;
    clientName?: string;
    attachments: PromptAttachment[];
  }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setGeneration({ phase: "streaming", steps: [], deltaChars: 0 });
    try {
      await cosmos.streamAdvisoryTlj(
        {
          prompt: input.prompt,
          clientName: input.clientName,
          attachments: input.attachments.map((attachment) => ({
            name: attachment.name,
            mimeType: attachment.mimeType,
            content: attachment.content
          }))
        },
        {
          onStep: (step) =>
            setGeneration((current) =>
              current.phase === "streaming"
                ? { ...current, steps: [...current.steps, step.label] }
                : current
            ),
          onDelta: (text) =>
            setGeneration((current) =>
              current.phase === "streaming"
                ? { ...current, deltaChars: current.deltaChars + text.length }
                : current
            ),
          onDone: (draft) => {
            setGeneration({ phase: "idle" });
            setDrafts((current) => [draft, ...current]);
            selectDraft(draft);
          }
        },
        { signal: controller.signal }
      );
    } catch (error) {
      if (controller.signal.aborted) {
        setGeneration({ phase: "idle" });
        return;
      }
      setGeneration({
        phase: "error",
        message:
          error instanceof Error ? error.message : "TLJ generation failed."
      });
    }
  };

  const changeStatus = async (
    draft: AdvisoryTljDraft,
    status: AdvisoryDraftStatus
  ) => {
    const previous = drafts;
    setDrafts((current) =>
      current.map((entry) => (entry.id === draft.id ? { ...entry, status } : entry))
    );
    if (selected?.id === draft.id) {
      setSelected({ ...draft, status });
    }
    try {
      await cosmos.updateAdvisoryDraft(draft.id, { status });
    } catch {
      setDrafts(previous);
    }
  };

  const removeDraft = async (draft: AdvisoryTljDraft) => {
    if (!window.confirm(`Delete the draft "${draft.title}"?`)) {
      return;
    }
    try {
      await cosmos.deleteAdvisoryDraft(draft.id);
      setDrafts((current) => current.filter((entry) => entry.id !== draft.id));
      if (selected?.id === draft.id) {
        selectDraft(null);
      }
    } catch (error) {
      setDraftsError(
        error instanceof Error ? error.message : "Could not delete the draft."
      );
    }
  };

  return (
    <div className="page mx-auto w-full max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Advisory"
        title="Tailored Learning Journeys"
        subtitle="Turn a client brief into a journey suggestion built from real AIHR catalog content — following the SME methodology, in the exact shape the LMS ingests. Suggestions only: nothing is created in the LMS."
        actions={
          <Button
            variant="secondary"
            onClick={() => selectDraft(null)}
            disabled={generation.phase === "streaming"}
          >
            <Plus className="h-4 w-4" />
            New suggestion
          </Button>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        <aside>
          <h2 className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Drafts
          </h2>
          {draftsError ? (
            <ErrorBlock message={draftsError} onRetry={() => void refreshDrafts()} />
          ) : (
            <AdvisoryDraftsList
              drafts={drafts}
              selectedId={selected?.id}
              onSelect={(draft) => selectDraft(draft)}
              onStatusChange={(draft, status) => void changeStatus(draft, status)}
              onDelete={(draft) => void removeDraft(draft)}
            />
          )}
        </aside>

        <main className="min-w-0">
          {composing && (
            <div className="space-y-4">
              <AdvisoryBriefComposer
                busy={generation.phase === "streaming"}
                onGenerate={(input) => void generate(input)}
              />
              {generation.phase === "streaming" && (
                <Card className="p-4">
                  <div className="flex items-center gap-2 text-sm text-anchor">
                    <Loader2 className="h-4 w-4 animate-spin text-[var(--primary)]" />
                    Designing the journey…
                  </div>
                  <ol className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {generation.steps.map((step, index) => (
                      <li key={`${step}-${index}`}>• {step}</li>
                    ))}
                  </ol>
                </Card>
              )}
              {generation.phase === "error" && (
                <ErrorBlock message={generation.message} />
              )}
            </div>
          )}

          {!composing && selected && <TljDraftView draft={selected} />}

          {!composing && !selected && (
            <EmptyState
              title="Pick a draft"
              body="Select a draft from the list or generate a new TLJ suggestion."
            />
          )}
        </main>
      </div>
    </div>
  );
}
